import { Info } from 'lucide-react';

interface MappingGuidanceProps {
  /** e.g. "class" or "family". */
  datasetLabel: string;
  requiredFields: ReadonlyArray<string>;
}

/**
 * Inline guidance for the column-mapping step (QA 2.3/2.4/2.5): explains which
 * fields are required, that optional fields can be left blank, and what to do
 * when a required column is missing from the source CSV.
 */
export function MappingGuidance({
  datasetLabel,
  requiredFields,
}: MappingGuidanceProps) {
  return (
    <details className='rounded-md border bg-muted/30 p-3 text-sm'>
      <summary className='flex cursor-pointer items-center gap-2 font-medium'>
        <Info className='size-4 text-muted-foreground' />
        How to map your {datasetLabel} columns
      </summary>
      <div className='mt-2 space-y-2 text-muted-foreground'>
        <p>
          Match each Enrollio field on the left to the matching column from your
          uploaded file. We pre-fill matches we recognize — double-check them.
        </p>
        <ul className='ml-4 list-disc space-y-1'>
          <li>
            <span className='font-medium text-foreground'>Required fields</span>{' '}
            are outlined in red and must be mapped to continue:{' '}
            {requiredFields.join(', ')}.
          </li>
          <li>
            <span className='font-medium text-foreground'>Optional fields</span>{' '}
            can be set to{' '}
            <span className='font-medium'>&quot;(Not provided)&quot;</span> or
            left blank — import the data you have and fill the rest in later.
          </li>
          <li>
            <span className='font-medium text-foreground'>
              Missing a required column?
            </span>{' '}
            Add it to your CSV and re-upload. You cannot finish mapping classes
            until every required field is mapped. Or use{' '}
            <span className='font-medium'>&quot;Skip classes&quot;</span> and
            add classes later.
          </li>
        </ul>
      </div>
    </details>
  );
}
